/**
 * Widget Catalog
 *
 * Joins the auto-generated widget-catalog.json (from the file system) with
 * the live widget registry to produce enriched catalog entries with derived tags.
 *
 * The JSON is generated by: bash scripts/generate-widget-catalog.sh
 * The file system (items/{category}/{widget}/) is the source of truth.
 */

import React from 'react';
import type { WidgetType } from './types';
import { widgetRegistry, WidgetDefinition, WIDGET_DATA_FLOW } from './registry';
import { WIDGET_CATEGORY_ICONS } from './widgetIcons';
import catalogJson from './widget-catalog.json';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CatalogJsonEntry {
  type: string;
  category: string;
  path: string;
}

export interface CatalogEntry {
  type: WidgetType;
  /** Directory category (e.g. "display", "input", "data-provider") */
  category: string;
  /** Relative path from items/ (e.g. "display/grid") */
  path: string;
  /** Full widget definition from the registry */
  definition: WidgetDefinition;
  /** Derived + custom tags for filtering */
  tags: string[];
}

// ---------------------------------------------------------------------------
// Tag display names — maps directory/derived tag keys to user-friendly labels
// ---------------------------------------------------------------------------

export const TAG_DISPLAY_NAMES: Record<string, string> = {
  'data-provider': 'Data Provider',
  'display': 'Display',
  'input': 'Input',
  'output': 'Output',
  'microsoft': 'Microsoft',
  'repository': 'Repository',
  'misc': 'Other',
  'system': 'System',
  'shareable': 'Shareable',
  'requires-credentials': 'Requires Credentials',
  'summarizable': 'Summarizable',
};

/** Get user-friendly tag label */
export function getTagLabel(tag: string): string {
  return TAG_DISPLAY_NAMES[tag] || tag.charAt(0).toUpperCase() + tag.slice(1).replace(/[-_]/g, ' ');
}

// ---------------------------------------------------------------------------
// Category display info — metadata for directory categories used by pickers
// ---------------------------------------------------------------------------

export interface CategoryInfo {
  label: string;
  icon: React.ReactNode;
  description: string;
}

/** Display metadata for each directory category. Used by DashboardBuilder picker, etc. */
export const CATEGORY_INFO: Record<string, CategoryInfo> = {
  'repository': { label: 'Repositories', icon: WIDGET_CATEGORY_ICONS.repositories, description: 'Store and manage images, PDFs, and notes' },
  'input': { label: 'Forms & Input', icon: WIDGET_CATEGORY_ICONS.input, description: 'Q&A forms, Conviction and Scorecard scoring' },
  'microsoft': { label: 'Microsoft', icon: WIDGET_CATEGORY_ICONS.microsoft, description: 'Excel spreadsheets and Outlook email' },
  'data-provider': { label: 'Data Provider', icon: WIDGET_CATEGORY_ICONS.external, description: 'Widgets backed by external data providers' },
  'misc': { label: 'External Data', icon: WIDGET_CATEGORY_ICONS.external, description: 'Connect to external data sources' },
  'display': { label: 'Display', icon: WIDGET_CATEGORY_ICONS.display, description: 'Static content — grids, charts, text, images' },
  'shared': { label: 'Shared from Dashboards', icon: WIDGET_CATEGORY_ICONS.shared, description: 'Widgets shared from other dashboards' },
};

/** All directory categories in display order */
export const CATEGORY_ORDER: string[] = [
  'repository', 'input', 'microsoft', 'data-provider', 'misc', 'display', 'shared',
];

// ---------------------------------------------------------------------------
// Build catalog entries
// ---------------------------------------------------------------------------

function deriveTags(entry: CatalogJsonEntry, def: WidgetDefinition): string[] {
  const tags = new Set<string>();

  // Category from directory structure
  tags.add(entry.category);

  // Derived from definition properties
  if (def.meta.isSystem) tags.add('system');
  if (def.meta.shareable) tags.add('shareable');
  if (def.meta.summaryEligible) tags.add('summarizable');

  // Data provider widgets require credentials
  if (def.dataProvider) {
    tags.add('data-provider');
    tags.add('requires-credentials');
  }

  // Data flow: input, output (display), or both
  const flow = WIDGET_DATA_FLOW[def.type];
  if (flow === 'display') {
    tags.add('output');
  } else if (flow === 'input') {
    tags.add('input');
  } else if (flow === 'both') {
    tags.add('input');
    tags.add('output');
  }

  // Microsoft widgets require credentials
  if (entry.category === 'microsoft') {
    tags.add('requires-credentials');
  }

  // Custom tags declared by the widget
  if (def.tags) {
    for (const t of def.tags) tags.add(t);
  }

  return Array.from(tags);
}

const entries: CatalogEntry[] = [];
const tagSet = new Set<string>();

for (const raw of catalogJson as CatalogJsonEntry[]) {
  const def = widgetRegistry.get(raw.type as WidgetType);
  if (!def) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[catalog] Widget "${raw.type}" is in widget-catalog.json but not in the registry. Run the generator or check registry.ts.`);
    }
    continue;
  }

  const tags = deriveTags(raw, def);
  tags.forEach(t => tagSet.add(t));

  entries.push({
    type: raw.type as WidgetType,
    category: raw.category,
    path: raw.path,
    definition: def,
    tags,
  });
}

// Warn about widgets in registry but not in catalog
if (process.env.NODE_ENV === 'development') {
  for (const [type] of Array.from(widgetRegistry)) {
    if (!entries.some(e => e.type === type)) {
      console.warn(`[catalog] Widget "${type}" is in the registry but not in widget-catalog.json. Run: bash scripts/generate-widget-catalog.sh`);
    }
  }
}

/** All enriched catalog entries, sorted by label */
export const catalogEntries: CatalogEntry[] = entries.sort(
  (a, b) => a.definition.meta.label.localeCompare(b.definition.meta.label),
);

/** All unique tags across all widgets, sorted alphabetically */
export const allTags: string[] = Array.from(tagSet).sort();

/** Lookup map: widget type → CatalogEntry */
export const catalogMap = new Map<WidgetType, CatalogEntry>(
  catalogEntries.map(e => [e.type, e]),
);
